import allFonts from './styleFonts';
import { fonts as systemFonts } from '@/assets/font-assets/fonts';
import { reactive } from 'vue';
import localforage from 'localforage';

localforage.config({
  name: 'font',
});
// Local fonts storage key
const LOCAL_FONTS_KEY = 'seede-local-fonts';

// Reactive local fonts array
export const localFonts = reactive([]);

// Load local fonts from localforage on init
async function loadLocalFontsFromStorage() {
  try {
    const fonts = await localforage.getItem(LOCAL_FONTS_KEY);
    if (fonts && Array.isArray(fonts)) {
      for (const font of fonts) {
        await registerFontFace(font.name, font.dataUrl);
        localFonts.push(font);
      }
    }
  } catch (e) {
    console.warn('Failed to load local fonts from storage:', e);
  }
}

// Save local fonts to localforage
async function saveLocalFontsToStorage() {
  try {
    // Create a plain array copy for storage (deep clone to remove reactive proxies)
    const fontsToStore = localFonts.map((font) => ({
      name: font.name,
      label: font.label,
      dataUrl: font.dataUrl,
      fileName: font.fileName,
      weights: font.weights.map((w) => ({ name: w.name, value: w.value })),
      isLocal: font.isLocal,
    }));
    await localforage.setItem(LOCAL_FONTS_KEY, fontsToStore);
  } catch (e) {
    console.warn('Failed to save local fonts to storage:', e);
  }
}

// Register a font using FontFace API
async function registerFontFace(fontName, dataUrl) {
  try {
    const fontFace = new FontFace(fontName, `url(${dataUrl})`);
    await fontFace.load();
    document.fonts.add(fontFace);
    return true;
  } catch (e) {
    console.error('Failed to register font:', e);
    return false;
  }
}

// Get font name from file name
function getFontNameFromFile(fileName) {
  // Remove extension and clean up name
  const name = fileName.replace(/\.(ttf|otf|woff|woff2)$/i, '');
  return name.replace(/[-_]/g, ' ');
}

// Add a local font from file
export async function addLocalFont(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'));
      return;
    }

    // Validate file type
    const validTypes = [
      'font/ttf',
      'font/otf',
      'font/woff',
      'font/woff2',
      'application/x-font-ttf',
      'application/x-font-otf',
      'application/font-woff',
      'application/font-woff2',
    ];

    const ext = file.name.split('.').pop().toLowerCase();
    const validExts = ['ttf', 'otf', 'woff', 'woff2'];

    if (!validExts.includes(ext)) {
      reject(new Error('Invalid font file type. Supported: TTF, OTF, WOFF, WOFF2'));
      return;
    }

    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const dataUrl = e.target.result;
        const fontName = getFontNameFromFile(file.name);

        // Check if font already exists
        if (localFonts.some((f) => f.name === fontName)) {
          reject(new Error('Font with this name already exists'));
          return;
        }

        // Register the font
        const success = await registerFontFace(fontName, dataUrl);
        if (!success) {
          reject(new Error('Failed to load font'));
          return;
        }

        // Add to local fonts
        const fontData = {
          name: fontName,
          label: fontName,
          dataUrl: dataUrl,
          fileName: file.name,
          weights: [{ name: 'Regular', value: '400' }],
          isLocal: true,
        };

        localFonts.push(fontData);
        await saveLocalFontsToStorage();

        resolve(fontData);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read font file'));
    };

    reader.readAsDataURL(file);
  });
}

// Remove a local font
export async function removeLocalFont(fontName) {
  const index = localFonts.findIndex((f) => f.name === fontName);
  if (index !== -1) {
    localFonts.splice(index, 1);
    await saveLocalFontsToStorage();
    return true;
  }
  return false;
}

// Initialize local fonts on module load
if (typeof window !== 'undefined') {
  // Delay to ensure DOM is ready
  setTimeout(loadLocalFontsFromStorage, 100);
}

// Check if the current platform is macOS
export function checkIsMacOS() {
  // Check if window and navigator are available (browser environment)
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  // Primary check using navigator.platform (more reliable)
  if (navigator.platform) {
    const platform = navigator.platform.toUpperCase();
    if (platform.indexOf('MAC') >= 0) {
      return true;
    }
  }

  // Fallback check using userAgent
  if (navigator.userAgent) {
    const userAgent = navigator.userAgent.toUpperCase();
    if (userAgent.indexOf('MAC') >= 0) {
      return true;
    }
  }

  // Additional check for newer browsers that may deprecate navigator.platform
  if (navigator.userAgentData && navigator.userAgentData.platform) {
    const platform = navigator.userAgentData.platform.toUpperCase();
    if (platform.indexOf('MAC') >= 0) {
      return true;
    }
  }

  return false;
}

export const fontFamilyOptions = [
  {
    name: 'English',
    list: [
      {
        name: 'Inter',
        label: 'Inter',
        url: 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap',
        weights: [
          {
            name: 'Thin',
            value: '300',
          },
          {
            name: 'Regular',
            value: '400',
          },
          {
            name: 'Semibold',
            value: '600',
          },
        ],
        style: 'Minimalist',
      },
      {
        name: 'DM Serif Text',
        label: 'DM Serif Text',
        url: 'https://fonts.googleapis.com/css2?family=DM+Serif+Text:ital@0;1&display=swap',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
        style: 'Classical',
      },

      {
        name: 'Poppins',
        label: 'Poppins',
        weights: [
          {
            name: 'Thin',
            value: '300',
          },
          {
            name: 'Regular',
            value: '400',
          },
          {
            name: 'Semibold',
            value: '600',
          },
          {
            name: 'Bold',
            value: '700',
          },
        ],
        style: 'Modern',
      },
      {
        name: 'Arial',
        label: 'Arial',
        weights: [
          {
            name: 'Thin',
            value: '300',
          },
          {
            name: 'Regular',
            value: '400',
          },
          {
            name: 'Semibold',
            value: '600',
          },
        ],
        style: 'Minimalist',
      },
      {
        name: 'Iglets',
        label: 'Iglets',
        url: 'https://static.seedeai.com/fonts/dmrh97/result.css',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
        style: 'Modern',
      },
      {
        name: 'Kelsi 1',
        label: 'Kelsi fill',
        url: 'https://static.seedeai.com/fonts/nx0ryl/result.css',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
        style: 'Modern',
      },
      {
        name: 'Better Fonts',
        label: 'Better Fonts',
        url: 'https://static.seedeai.com/fonts/4qdpsk/result.css',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
        style: 'Vintage',
      },
      {
        name: 'Harshita',
        label: 'Harshita',
        url: 'https://static.seedeai.com/fonts/lb2psq/result.css',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
        style: 'Modern',
      },

      {
        name: 'FRIDAY LOVERS',
        label: 'FRIDAY LOVERS',
        url: 'https://static.seedeai.com/fonts/mwxa3h/result.css',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
        style: 'Modern',
      },
      {
        name: 'Rubik Wet Paint',
        label: 'Rubik Wet Paint',
        url: 'https://fonts.googleapis.com/css2?family=Rubik+Wet+Paint&display=swap',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
        style: 'Modern',
      },
      {
        name: 'Mochiy Pop One',
        label: 'Mochiy Pop One',
        url: 'https://fonts.googleapis.com/css2?family=Mochiy+Pop+One&display=swap',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
        style: 'Bold',
      },
      ...allFonts,
      {
        name: 'Nabla',
        label: 'Nabla',
        url: 'https://fonts.googleapis.com/css2?family=Nabla&display=swap',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
        style: 'Modern',
      },
    ],
  },
  {
    name: 'Chinese',
    list: [
      {
        name: 'Noto Sans',
        label: '思源黑体',
        url: 'https://static.seedeai.com/fonts/f3njky/result.css',
        style: '常规',
        styleDesc: '常规字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
          {
            name: 'Bold',
            value: '700',
          },
        ],
      },
      {
        name: 'Noto Serif SC',
        label: '思源宋体',
        url: 'https://fonts.googleapis.com/css2?family=Noto+Serif+SC:wght@400;700&display=swap',
        style: '常规',
        styleDesc: '常规字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
          {
            name: 'Bold',
            value: '700',
          },
        ],
      },
      {
        name: 'Source Han Sans SC VF',
        label: '思源黑体VF',
        url: 'https://static.seedeai.com/fonts/a6qbdy/result.css',
        style: '常规',
        styleDesc: '常规字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'OPPO Sans 4.0',
        label: 'OPPO Sans',
        url: ' https://static.seedeai.com/fonts/rtfbqb/result.css',
        style: '常规',
        styleDesc: '常规字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'Chill Round Gothic',
        label: '寒蝉圆黑体',
        url: 'https://static.seedeai.com/fonts/b1p97w/result.css',
        style: '常规',
        styleDesc: '常规字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: '江城圆体 400W',
        label: '江城圆体',
        url: 'https://static.seedeai.com/fonts/gksjfh/result.css',
        style: '常规',
        styleDesc: '常规字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'Smiley Sans Oblique',
        label: '得意黑',
        url: 'https://static.seedeai.com/fonts/b8ic3q/result.css',
        style: '活泼',
        styleDesc: '活泼字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'Douyin Sans',
        label: '抖音美好体',
        url: 'https://static.seedeai.com/fonts/85x375/result.css',
        style: '活泼',
        styleDesc: '活泼字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: '荆南波波黑',
        label: '荆南波波黑',
        url: 'https://static.seedeai.com/fonts/3g3lev/result.css',
        style: '活泼',
        styleDesc: '活泼字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'zcoolwenyiti',
        label: '站酷文艺体',
        url: 'https://static.seedeai.com/fonts/o4wmmb/result.css',
        style: '活泼',
        styleDesc: '活泼字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'LXGW WenKai GB',
        label: '霞鹜文楷 GB',
        url: 'https://static.seedeai.com/fonts/vilv71/result.css',
        style: '活泼',
        styleDesc: '活泼字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'TsangerShuYuanT W03',
        label: '仓耳舒圆体',
        url: 'https://static.seedeai.com/fonts/s9b2jv/result.css',
        style: '活泼',
        styleDesc: '活泼字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'MaoKenWangXingYuan',
        label: '猫啃忘形圆',
        url: 'https://static.seedeai.com/fonts/wvbzi1/result.css',
        style: '活泼',
        styleDesc: '活泼字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'Child Fun Sans Fusion G',
        label: '游趣体 Sans',
        url: 'https://static.seedeai.com/fonts/gsmyic/result.css',
        style: '活泼',
        styleDesc: '活泼字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: '字库江湖古风体 常规',
        label: '字库江湖古风体',
        url: 'https://static.seedeai.com/fonts/dsonam/result.css',
        style: '活泼',
        styleDesc: '活泼字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'MaokenAssortedSans',
        label: '猫啃什锦黑',
        url: 'https://static.seedeai.com/fonts/kxtlc7/result.css',
        style: '卡通',
        styleDesc: '卡通字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'XiangcuiZeroHei-2.0',
        label: '香萃零度黑',
        url: 'https://static.seedeai.com/fonts/qoc1yp/result.css',
        style: '卡通',
        styleDesc: '卡通字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'TsangerXWZ',
        label: '仓耳小丸子',
        url: 'https://static.seedeai.com/fonts/43n6wl/result.css',
        style: '卡通',
        styleDesc: '卡通字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'TsangerFeiBai W03',
        label: '仓耳非白',
        url: 'https://static.seedeai.com/fonts/aevqz4/result.css',
        style: '卡通',
        styleDesc: '卡通字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'MisekiBitmapMincho',
        label: '美绩点阵体',
        url: 'https://static.seedeai.com/fonts/1h0awx/result.css',
        style: '卡通',
        styleDesc: '卡通字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: '平方赖江湖琅琊体',
        label: '平方赖江湖琅琊体',
        url: 'https://static.seedeai.com/fonts/c29rv1/result.css',
        style: '书法',
        styleDesc: '书法字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'PangMenZhengDao-Cu',
        label: '庞门正道粗书体',
        url: 'https://static.seedeai.com/fonts/e44ei1/result.css',
        style: '书法',
        styleDesc: '书法字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'Slidefu',
        label: '演示佛系体',
        url: 'https://static.seedeai.com/fonts/zv3ng6/result.css',
        style: '书法',
        styleDesc: '书法字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'Slidexiaxing',
        label: '演示夏行楷',
        url: 'https://static.seedeai.com/fonts/stbo5k/result.css',
        style: '书法',
        styleDesc: '书法字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'Long Cang',
        label: '有字库龙藏体',
        url: 'https://static.seedeai.com/fonts/s312v3/result.css',
        style: '书法',
        styleDesc: '书法字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'chengrongguangke',
        label: '程荣光刻楷',
        url: 'https://static.seedeai.com/fonts/nmvej6/result.css',
        style: '书法',
        styleDesc: '书法字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'XuandongKaishu',
        label: '玄冬楷书',
        url: 'https://static.seedeai.com/fonts/5u7th0/result.css',
        style: '书法',
        styleDesc: '书法字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: '新叶念体',
        label: '新叶念体',
        url: 'https://static.seedeai.com/fonts/q2x9q8/result.css',
        style: '手写',
        styleDesc: '手写字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'Muyao-Softbrush',
        label: '沐瑶软笔手写体',
        url: 'https://static.seedeai.com/fonts/7t3bjk/result.css',
        style: '手写',
        styleDesc: '手写字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: '851tegakizatsu',
        label: '851手书',
        url: 'https://static.seedeai.com/fonts/6d2mzv/result.css',
        style: '手写',
        styleDesc: '手写字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'bailutongtongshouxieti',
        label: '白路彤彤手写体',
        url: 'https://static.seedeai.com/fonts/r6dz29/result.css',
        style: '手写',
        styleDesc: '手写字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'PING FANG XING CHEN',
        label: '平方星辰体',
        url: 'https://static.seedeai.com/fonts/eexpm6/result.css',
        style: '手写',
        styleDesc: '手写字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'Huiwen-ZhengKai',
        label: '汇文正楷',
        url: 'https://static.seedeai.com/fonts/nrg5v9/result.css',
        style: '手写',
        styleDesc: '手写字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'LanternMingA',
        label: '上元明朝 A',
        url: 'https://static.seedeai.com/fonts/pkwjx1/result.css',
        style: '刚劲',
        styleDesc: '刚劲字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'Huiwen-mincho',
        label: '汇文明朝体',
        url: 'https://static.seedeai.com/fonts/dsqdzq/result.css',
        style: '刚劲',
        styleDesc: '刚劲字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'QIJIC',
        label: '令东齐伋复刻体',
        url: 'https://static.seedeai.com/fonts/s2dl4q/result.css',
        style: '刚劲',
        styleDesc: '刚劲字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'ziticqnananti',
        label: '字体传奇南安体',
        url: 'https://static.seedeai.com/fonts/bowf3n/result.css',
        style: '刚劲',
        styleDesc: '刚劲字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'ToneOZ-Pinyin-Kai-Simplified',
        label: '拼音鼎楷',
        url: 'https://static.seedeai.com/fonts/6dg81n/result.css',
        style: '拼音',
        styleDesc: '拼音字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
      {
        name: 'Mengshen',
        label: '萌神拼音字体',
        url: 'https://static.seedeai.com/fonts/kbo900/result.css',
        style: '拼音',
        styleDesc: '拼音字体',
        weights: [
          {
            name: 'Regular',
            value: '400',
          },
        ],
      },
    ],
  },
];

function createFontStyle(url) {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  document.head.appendChild(link);
}

export async function prepareSystemFontFamily() {
  if (window.api) {
    if (window.api.loadSystemFont) {
      const systemFonts = await window.api.loadSystemFont();
      systemFonts.forEach((collection) => {
        createFontStyle(`${collection.server}/fonts.css`);
        fontFamilyOptions.push({
          name: collection.name || 'System',
          list: collection.fonts,
        });
      });
    }
  } else {
    setTimeout(() => {
      fontFamilyOptions.push({
        name: 'System',
        list: systemFonts,
      });
    }, 200);
  }
}

export function findFontByName(name) {
  for (const family of fontFamilyOptions) {
    for (const font of family.list) {
      if (font.name === name) {
        return font;
      }
    }
  }

  return null;
}
